import { useCallback, useMemo, useRef, useState } from 'react';
import { Document, pdfjs, Page, DocumentProps } from 'react-pdf';

import * as Sentry from '@breeze/web-lib-sentry';
import cn from 'classnames';
import useResizeObserver, { ResizeHandler } from 'use-resize-observer';

import styles from './pdf-preview.module.css';

import { PdfPreviewFallback } from '../pdf-preview-fallback/pdf-preview-fallback';
import { PdfPreviewSkeleton } from '../pdf-preview-skeleton/pdf-preview-skeleton';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

export type PdfPreviewProps = {
  pdfUrl: string;
  title: string | undefined;
};

export const PdfPreview = ({ pdfUrl, title }: PdfPreviewProps) => {
  const [numPages, setNumPages] = useState<number>();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>();

  const onLoadSuccess: DocumentProps['onLoadSuccess'] = ({
    numPages: nextNumPages,
  }) => {
    setNumPages(nextNumPages);
  };

  const onResize: ResizeHandler = useCallback((size) => {
    const width = size.width ?? 0;
    setContainerWidth(Math.min(width, 800));
  }, []);

  useResizeObserver({ ref: containerRef, onResize });

  const previewFile = useMemo(() => ({ url: pdfUrl }), [pdfUrl]);

  return (
    <div
      ref={containerRef}
      className={cn(styles.root, 'flex w-full flex-col items-center')}
    >
      <h2 className="sr-only">{title}</h2>
      <Sentry.ErrorBoundary
        fallback={<PdfPreviewFallback pdfUrl={pdfUrl} title={title} />}
      >
        <Document
          file={previewFile}
          onLoadSuccess={onLoadSuccess}
          loading={<PdfPreviewSkeleton />}
          error={<PdfPreviewFallback pdfUrl={pdfUrl} title={title} />}
        >
          {Array.from(new Array(numPages), (_el, index) => (
            <Page
              loading={<PdfPreviewSkeleton numPages={2} />}
              key={`page_${index + 1}`}
              pageNumber={index + 1}
              width={containerWidth}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
          ))}
        </Document>
      </Sentry.ErrorBoundary>
    </div>
  );
};
